import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useEffect } from 'react';
import { DictionaryEntry } from '../types';
import { DefinitionFooter } from './DefinitionFooter';

interface ModalProps {
  entry: DictionaryEntry | null;
  onClose: () => void;
}

export const Modal = ({ entry, onClose }: ModalProps) => {
  // Fermer avec Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (entry) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
    };
  }, [entry, onClose]);

  return (
    <AnimatePresence>
      {entry && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 overflow-y-auto"
        >
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl p-8 max-w-4xl w-full shadow-2xl relative my-8"
            >

              {/* Contenu */}
              <div className="">
                {/* Header */}
                <div className="mb-6">
                  <h2 className="text-5xl md:text-6xl font-sans font-black text-amber-500 tracking-tight">
                    {entry.term}
                  </h2>
                </div>

                {/* Définition courte (Lead - Style Papier) */}
                <div className="mb-8">
                  <p className="text-xl md:text-2xl text-sky-500 font-sans leading-snug">
                    <span className="font-bold mr-2">{entry.type}</span>
                    {entry.definition.split(' ').slice(0, -1).join(' ')} <span className="whitespace-nowrap">{entry.definition.split(' ').slice(-1)}</span>
                  </p>
                </div>

                {/* Image d'illustration */}
                {entry.image && (
                  <div className="mb-8 rounded-2xl overflow-hidden shadow-sm max-w-2xl mx-auto border border-slate-100 bg-slate-50">
                    <div className="aspect-square w-full">
                      <img
                        src={entry.image}
                        alt={entry.term}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}

                {/* Définition complète */}
                <div className="prose prose-slate prose-lg md:prose-xl max-w-none mb-10">
                  {entry.fullDefinition.split(/\n|\\n/).map((paragraph, index) => (
                    <p key={index} className="text-slate-800 leading-relaxed mb-6 last:mb-0">
                      {paragraph.trim()}
                    </p>
                  ))}
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-3 pt-6 border-t border-slate-100">
                  {entry.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-4 py-2 bg-slate-50 text-slate-500 text-sm font-semibold rounded-2xl border border-slate-100 hover:bg-slate-100 transition-colors cursor-default"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                {/* Footer */}
                <DefinitionFooter entry={entry} />
              </div>

              {/* Bouton fermer */}
              <button
                onClick={onClose}
                className="absolute top-6 right-6 p-2 text-slate-300 hover:text-amber-500 hover:bg-amber-50 rounded-full transition-all duration-300 z-50 group/close"
                aria-label="Fermer"
              >
                <X size={28} className="group-hover/close:rotate-90 transition-transform duration-300" />
              </button>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
